import type { LoginNextStepV1 } from "@strust/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { ApiRequestError } from "../../../shared/api/client.ts";
import { assignLocation } from "../../../shared/navigation.ts";
import { useAcceptInvitation, useInvitation } from "../api/session.ts";
import { InvitationScreen, invitationLinkClass } from "../components/invitation-screen.tsx";
import { invitationMessages } from "../messages.ts";
import { describeError, isCommonPasswordRefusal } from "../model/error-message.ts";
import { LoginStep } from "./login-step.tsx";

const HOUR_MS = 3_600_000;

/** The page an invitation link opens: inspect it, then set the password (or continue with SSO). */
export function InvitationPage({ token }: { readonly token: string }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const accept = useAcceptInvitation(token);
  // Once accepted (or while accepting), the link is consumed: never read it again.
  const consumed = accept.isPending || accept.isSuccess;
  const invitation = useInvitation(token, !consumed);
  // Fixed at first render: the badge shows the hours left when the page opened.
  const [openedAt] = useState(() => Date.now());
  // An administrator, or a two-factor recovery, sets an authenticator up before the first session.
  const [step, setStep] = useState<
    { readonly next: LoginNextStepV1; readonly recovery: boolean } | undefined
  >();
  const goHome = useCallback(() => {
    void navigate({ to: "/" });
  }, [navigate]);
  const toLogin = useCallback(() => {
    void navigate({ to: "/login" });
  }, [navigate]);

  if (step !== undefined)
    return (
      <LoginStep
        step={step.next}
        recovery={step.recovery}
        onSignedIn={goHome}
        onRestart={toLogin}
      />
    );

  // Accepted with a session: the cache was cleared and the app is navigating home.
  if (accept.isSuccess || invitation.isPending) return <InvitationScreen state="loading" />;

  if (invitation.isError) {
    const status = invitation.error instanceof ApiRequestError ? invitation.error.status : 0;
    const message = describeError(intl, invitation.error);
    return status >= 400 && status < 500 && status !== 429 ? (
      <InvitationScreen
        state="unavailable"
        message={message}
        signInLink={
          <Link to="/login" className={invitationLinkClass}>
            {intl.formatMessage(invitationMessages.goToLogin)}
          </Link>
        }
      />
    ) : (
      <InvitationScreen
        state="failed"
        message={message}
        onRetry={() => void invitation.refetch()}
      />
    );
  }

  const view = invitation.data;
  return (
    <InvitationScreen
      state="ready"
      invitation={view}
      hoursLeft={Math.max(0, Math.ceil((Date.parse(view.expires_at) - openedAt) / HOUR_MS))}
      pending={accept.isPending}
      error={accept.error ? describeError(intl, accept.error) : undefined}
      commonRefused={isCommonPasswordRefusal(accept.error)}
      onSso={() => {
        assignLocation(`/explorer/v1/auth/sso/start?invitation=${encodeURIComponent(token)}`);
      }}
      onSubmit={(password) => {
        const recovery = view.kind === "two_factor_recovery";
        accept.mutate(
          { password },
          {
            onSuccess: (outcome) => {
              if (outcome.kind === "step") setStep({ next: outcome.step, recovery });
              else goHome();
            },
          },
        );
      }}
    />
  );
}
